/**
 * Intelligent Duplicate Detection & Data Normalization Utility
 * Handles smart duplication detection considering multiple fields, not just site_name
 */

export interface DuplicateCheckOptions {
  checkFields: string[];  // Fields to consider for duplication check
  normalizeFields?: Record<string, (val: any) => any>;  // Field normalization functions
  excludeDuplicates?: boolean;  // If true, exclude duplicates; if false, flag them
}

/**
 * Normalize data_resolution to allowed format
 * Handles various input formats and converts them to standard format
 */
export const normalizeDataResolution = (value: string | null | undefined): string => {
  if (!value) return '5m';  // Default
  
  const val = String(value).trim().toLowerCase();
  
  // Direct mappings — covers common abbreviations and long-form inputs
  const resolutionMap: Record<string, string> = {
    // Minutes
    '1m': '1m',   '1min': '1m',   '1minute': '1m',   '1 min': '1m',
    '2m': '2m',   '2min': '2m',   '2minute': '2m',
    '5m': '5m',   '5min': '5m',   '5minute': '5m',   '5 min': '5m',
    '10m': '10m', '10min': '10m', '10minute': '10m',
    '15m': '15m', '15min': '15m', '15minute': '15m',
    '30m': '30m', '30min': '30m', '30minute': '30m',
    // Hours
    '1h': '1h',   '1hr': '1h',    '1hour': '1h',    '1 hour': '1h',    '60m': '1h',   '60min': '1h',
    '2h': '2h',   '2hr': '2h',    '2hour': '2h',    '120m': '2h',
    '4h': '4h',   '4hr': '4h',    '4hour': '4h',    '240m': '4h',
    '6h': '6h',   '6hr': '6h',    '6hour': '6h',    '360m': '6h',
    '12h': '12h', '12hr': '12h',  '12hour': '12h',   '720m': '12h',
    'hourly': '1h',
    // Days
    '1d': '1d',   '1day': '1d',   '1 day': '1d',     'daily': '1d',
    '2d': '1d',   '3d': '1d',  // multi-day → fall back to 1d
  };
  
  return resolutionMap[val] || '5m';
};

/**
 * Create a normalized composite key for duplicate detection
 * Uses multiple fields to create a unique signature
 */
export const createDuplicateKey = (
  row: Record<string, any>,
  checkFields: string[]
): string => {
  return checkFields
    .map(field => {
      const val = row[field];
      if (val === null || val === undefined) return '';
      return String(val).trim().toLowerCase();
    })
    .filter(v => v.length > 0)
    .join('::');
};

/**
 * Detect and handle duplicate records intelligently
 * Considers site_name + location + company for uniqueness
 */
export const detectIntelligentDuplicates = (
  rows: Record<string, any>[],
  options: DuplicateCheckOptions = {
    checkFields: ['site_name', 'location', 'company_name'],
    excludeDuplicates: true,
  }
): {
  validRows: Record<string, any>[];
  duplicateRows: Array<{ row: Record<string, any>; reason: string; rowIndex: number }>;
  normalizedRows: Record<string, any>[];
} => {
  const seen = new Set<string>();
  const validRows: Record<string, any>[] = [];
  const duplicateRows: Array<{ row: Record<string, any>; reason: string; rowIndex: number }> = [];
  const normalizedRows: Record<string, any>[] = [];

  rows.forEach((row, index) => {
    const normalized = { ...row };

    // Apply normalization functions
    if (options.normalizeFields) {
      Object.entries(options.normalizeFields).forEach(([field, normalizer]) => {
        if (field in normalized) {
          normalized[field] = normalizer(normalized[field]);
        }
      });
    }

    // Auto-normalize data_resolution if present
    if ('data_resolution' in normalized) {
      normalized.data_resolution = normalizeDataResolution(normalized.data_resolution);
    }

    normalizedRows.push(normalized);

    // Create composite key for duplicate detection
    const key = createDuplicateKey(normalized, options.checkFields);

    if (!key) {
      // No valid key created, skip row
      duplicateRows.push({
        row: normalized,
        reason: 'Missing required fields for duplicate detection',
        rowIndex: index + 2, // +2 because sheet starts at row 1, headers at row 1
      });
      return;
    }

    if (seen.has(key)) {
      duplicateRows.push({
        row: normalized,
        reason: `Duplicate found: ${options.checkFields.join(', ')} combination already exists`,
        rowIndex: index + 2,
      });
    } else {
      seen.add(key);
      validRows.push(normalized);
    }
  });

  return {
    validRows: options.excludeDuplicates ? validRows : rows,
    duplicateRows,
    normalizedRows,
  };
};

/**
 * Validate sheet data against schema and normalize
 */
export const normalizeAndValidateSheet = (
  rows: Record<string, any>[],
  schema: any = {}
): {
  validRows: Record<string, any>[];
  errors: Array<{ rowIndex: number; field: string; error: string }>;
  warnings: Array<{ rowIndex: number; field: string; warning: string }>;
} => {
  const errors: Array<{ rowIndex: number; field: string; error: string }> = [];
  const warnings: Array<{ rowIndex: number; field: string; warning: string }> = [];
  const validRows: Record<string, any>[] = [];

  rows.forEach((row, index) => {
    const normalized = { ...row };
    let hasError = false;

    // Normalize specific fields
    if ('data_resolution' in normalized) {
      const original = normalized.data_resolution;
      normalized.data_resolution = normalizeDataResolution(original);
      
      if (original && original !== normalized.data_resolution) {
        warnings.push({
          rowIndex: index + 2,
          field: 'data_resolution',
          warning: `Auto-corrected from "${original}" to "${normalized.data_resolution}"`,
        });
      }
    }

    // Normalize site_name (trim, capitalize)
    if ('site_name' in normalized && normalized.site_name) {
      normalized.site_name = String(normalized.site_name).trim();
    }

    // Normalize location (trim, capitalize first letter of each word)
    if ('location' in normalized && normalized.location) {
      normalized.location = String(normalized.location)
        .trim()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    if (!hasError) {
      validRows.push(normalized);
    }
  });

  return {
    validRows,
    errors,
    warnings,
  };
};

/**
 * Generate duplicate detection summary for UI
 */
export const generateDuplicateSummary = (
  duplicates: Array<{ row: Record<string, any>; reason: string; rowIndex: number }>,
  checkFields: string[] = ['site_name', 'location', 'company_name']
): {
  message: string;
  details: string[];
  count: number;
} => {
  const details = duplicates.map(dup => {
    const fieldValues = checkFields
      .map(field => `${field}: "${dup.row[field] || 'N/A'}"`)
      .join(', ');
    return `Row ${dup.rowIndex} (${fieldValues}) - ${dup.reason}`;
  });

  return {
    count: duplicates.length,
    message: `Found ${duplicates.length} potential duplicate(s) in upload sheet`,
    details,
  };
};
